import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import InstagramEmbed from "@/components/InstagramEmbed";
import SmsPopup from "@/components/SmsPopup";
import { useEvents } from "@/hooks/useEvents";
import { useCanonical } from "@/hooks/useCanonical";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { trackCtaClick, trackVendorApplyClick } from "@/lib/analytics";
import ctaBackground from "@/assets/cta-background.png";


/**
 * Build a Date targeting the event start time in US Central Time.
 */
function buildCentralTargetDate(dateStr: string, timeStr: string | null): Date {
  let hours = 12;
  let minutes = 0;
  if (timeStr) {
    const startTime = timeStr.split("-")[0].trim();
    const match = startTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) {
      hours = parseInt(match[1], 10);
      minutes = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && hours !== 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
    }
  }
  const [year, month] = dateStr.split("-").map(Number);
  const eventDate = new Date(year, month - 1, parseInt(dateStr.split("-")[2], 10));
  const mar1 = new Date(year, 2, 1);
  const dstStart = new Date(year, 2, 8 + ((7 - mar1.getDay()) % 7), 2);
  const nov1 = new Date(year, 10, 1);
  const dstEnd = new Date(year, 10, 1 + ((7 - nov1.getDay()) % 7), 2);
  const isDST = eventDate >= dstStart && eventDate < dstEnd;
  const offset = isDST ? "-05:00" : "-06:00";
  const h = hours.toString().padStart(2, "0");
  const m = minutes.toString().padStart(2, "0");
  return new Date(`${dateStr}T${h}:${m}:00${offset}`);
}

const Index = () => {
  useCanonical("/");
  const { data: events } = useEvents();

  // SEO metadata
  useEffect(() => {
    const title = "Sole to Sole | June 20 Dallas Sneaker Event — Dallas Market Hall";
    const desc = "Sole to Sole returns June 20, 2026 at Dallas Market Hall. DFW's largest sneaker buy, sell, trade event with 80+ vendors, streetwear, and vintage. Get tickets now.";
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", desc);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", desc);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = events
    ?.filter((event) => {
      const [year, month, day] = event.date.split("-").map(Number);
      return new Date(year, month - 1, day) >= today;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const pastEvents = events
    ?.filter((event) => {
      const [year, month, day] = event.date.split("-").map(Number);
      return new Date(year, month - 1, day) < today;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 2);

  const currentEvent = upcomingEvents?.[0] ?? null;
  const countdownDate = currentEvent
    ? buildCentralTargetDate(currentEvent.date, currentEvent.time)
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <SmsPopup currentEvent={currentEvent} countdownDate={countdownDate} />
        {/* Hero — Current Event Module */}
        <HeroSection currentEvent={currentEvent} countdownDate={countdownDate} />

        {/* From the Floor — Instagram Embed */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <h2 className="font-heading text-3xl font-bold mb-8 text-center">
              FROM THE FLOOR
            </h2>
            <InstagramEmbed postUrl="https://www.instagram.com/reel/DVd9juUje3f/" />
          </div>
        </section>

        {/* Positioning Block */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold leading-relaxed md:text-2xl">
              June 20, 2026 Dallas Sneaker Event — Reviving the Community
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl mt-3">
              Dallas deserves a real marketplace. Not a pop-up. Not a showcase.
            </p>
            <p className="text-muted-foreground text-lg md:text-xl mt-1">
              A place where collectors, resellers, and culture connect.
            </p>
            <p className="text-muted-foreground text-base md:text-lg mt-6">
              <strong className="text-foreground">Sole to Sole</strong> is a Dallas-based sneaker buy, sell, and trade event and marketplace held at Dallas Market Hall.{" "}
              <Link to="/about" className="text-primary hover:underline font-medium">
                Learn more about Sole to Sole →
              </Link>
            </p>
          </div>
        </section>

        {/* Vendor CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div
              className="rounded-2xl p-8 md:p-12 text-center relative overflow-hidden bg-cover bg-center"
              style={{ backgroundImage: `url(${ctaBackground})` }}
            >
              <h2 className="font-heading text-3xl font-bold text-primary-foreground mb-6">
                Want a table at this event?
              </h2>

              {/* 3-step process */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-0 mb-8">
                {[
                  { num: "1", label: "Apply", desc: "Fill out the vendor form" },
                  { num: "2", label: "Review", desc: "We review your application" },
                  { num: "3", label: "Set Up", desc: "Get your table and sell" },
                ].map((step, i) => (
                  <div key={step.num} className="flex items-center">
                    <div className="flex flex-col items-center text-center w-36">
                      <div className="w-10 h-10 rounded-full border-2 border-white/60 flex items-center justify-center text-primary-foreground font-bold text-sm">
                        {step.num}
                      </div>
                        <span className="text-primary-foreground font-heading font-bold text-sm mt-2">{step.label}</span>
                        <span className="text-primary-foreground/70 text-xs mt-0.5 font-semibold">{step.desc}</span>
                    </div>
                    {i < 2 && (
                      <div className="hidden sm:block w-12 h-px bg-white/40 mx-1" />
                    )}
                  </div>
                ))}
              </div>

              <Link
                to="/apply"
                onClick={() => {
                  trackCtaClick("apply_now_homepage", "/apply");
                  trackVendorApplyClick("homepage_cta");
                }}
              >
                <Button size="lg" variant="secondary" className="gap-2">
                  Apply Now
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Past Events */}
        {pastEvents && pastEvents.length > 0 && (
          <section className="py-16 bg-secondary/30">
            <div className="container mx-auto px-4">
              <h2 className="font-heading text-2xl font-bold mb-8 text-center text-muted-foreground">
                PAST EVENTS
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {pastEvents.map((event) => (
                  <div
                    key={event.id}
                    className="rounded-xl overflow-hidden border border-border bg-card"
                  >
                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full aspect-[4/3] object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-sm">
                        {event.title}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-heading text-3xl font-bold mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "What is Sole To Sole?",
                  a: "Sole To Sole is a Dallas-based sneaker buy, sell, and trade event and marketplace. We host large-scale sneaker conventions at Dallas Market Hall where collectors, resellers, brands, and enthusiasts come together.",
                },
                {
                  q: "When is the next Sole To Sole event?",
                  a: "The next Sole To Sole event is June 20, 2026 at Dallas Market Hall — 2200 N Stemmons Fwy, Dallas, TX 75247. Doors open at 12:00 PM. Grab your tickets early — they sell out fast!",
                },
                {
                  q: "Where is Sole To Sole held?",
                  a: "Sole To Sole events take place at Dallas Market Hall, located at 2200 N Stemmons Fwy, Dallas, TX 75247 — one of the premier event venues in North Texas.",
                },
                {
                  q: "Who should attend a Sole To Sole event?",
                  a: "Anyone who loves sneakers, streetwear, or vintage fashion! Whether you're a collector looking for rare grails, a reseller growing your business, or just someone who wants to experience sneaker culture in Dallas — Sole To Sole is for you.",
                },
                {
                  q: "How do I become a vendor?",
                  a: "Visit our vendor application page to apply for a booth at the next Sole To Sole sneaker event in Dallas. We welcome sneaker resellers, streetwear brands, vintage sellers, artists, and food vendors.",
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="rounded-lg border border-border bg-card p-4 group"
                >
                  <summary className="font-medium cursor-pointer list-none flex items-center justify-between">
                    {faq.q}
                    <span className="text-muted-foreground group-open:rotate-180 transition-transform">
                      ▾
                    </span>
                  </summary>
                  <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "What is Sole To Sole?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Sole To Sole is a Dallas-based sneaker buy, sell, and trade event and marketplace. We host large-scale sneaker conventions at Dallas Market Hall where collectors, resellers, brands, and enthusiasts come together.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "When is the next Sole To Sole event?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "The next Sole To Sole event is June 20, 2026 at Dallas Market Hall — 2200 N Stemmons Fwy, Dallas, TX 75247.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Where is Sole To Sole held?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Sole To Sole events take place at Dallas Market Hall, located at 2200 N Stemmons Fwy, Dallas, TX 75247.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Who should attend a Sole To Sole event?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Anyone who loves sneakers, streetwear, or vintage fashion — collectors, resellers, and enthusiasts alike.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How do I become a vendor?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Visit the vendor application page on soletosole.com to apply for a booth at the next sneaker event in Dallas.",
                    },
                  },
                ],
              }),
            }}
          />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
